import { useQConfig } from '@exam-management/feature-qconfig-provider';
import { QConfigStatus, useGetDisabledLosInQConfigQuery } from '@exam-management/graphql';
import { Alert, AlertTitle, List, ListItem, Text } from '@shared/components';
import { useMemo } from 'react';

import { sortLosByStructure } from '../../utils';

const FALLBACK_LEVEL_NAME = 'ไม่ระบุ';

const DISABLED_LOS_IN_Q_CONFIG_ALERT_TEXT = {
  [QConfigStatus.Draft]: {
    title: 'มี LOS ที่ถูกปิดการใช้งาน (Disable LOS)',
    subtitle: 'กรุณาตรวจสอบและเปลี่ยน LOS ต่อไปนี้:',
  },
  [QConfigStatus.Published]: {
    title: 'QConfig นี้ไม่สามารถถูกนำไปจัดชุดข้อสอบได้',
    subtitle: 'เนื่องจากมี LOS ที่ถูกปิดการใช้งาน (Disable LOS) ดังต่อไปนี้:',
  },
  [QConfigStatus.Disabled]: {
    title: 'QConfig นี้ไม่สามารถถูกนำไปจัดชุดข้อสอบได้',
    subtitle: 'เนื่องจากมี LOS ที่ถูกปิดการใช้งาน (Disable LOS) ดังต่อไปนี้:',
  },
};

export default function DisabledLosInQConfigAlert() {
  const {
    qConfigData: { id: qConfigId, structures: qConfigStructures, status: qConfigStatus },
    publishQConfig: { errors: publishErrors },
  } = useQConfig();

  const { data: disabledLosData } = useGetDisabledLosInQConfigQuery({
    variables: {
      getDisabledLearningObjectiveStatementsByQConfigIdInput: {
        qConfigId,
      },
    },
    skip: !qConfigId,
  });

  const disabledLos = useMemo(() => {
    const disabledLosArray =
      disabledLosData?.excGetDisabledLearningObjectiveStatementsByQConfigId
        .disabledLearningObjectiveStatements;

    if (!disabledLosArray || disabledLosArray.length === 0) {
      return [];
    }

    return sortLosByStructure(disabledLosArray, qConfigStructures);
  }, [disabledLosData, qConfigStructures]);

  const shouldHideAlert = disabledLos.length === 0 || (publishErrors && publishErrors.length > 0);

  if (shouldHideAlert) {
    return null;
  }

  return (
    <Alert
      severity='error'
      sx={(theme) => ({
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        '& .MuiAlert-message': {
          overflow: 'unset',
        },
      })}
    >
      <AlertTitle>{DISABLED_LOS_IN_Q_CONFIG_ALERT_TEXT[qConfigStatus]?.title}</AlertTitle>
      <Text variant='body2'>{DISABLED_LOS_IN_Q_CONFIG_ALERT_TEXT[qConfigStatus]?.subtitle}</Text>
      <List
        sx={(theme) => ({
          listStyle: 'disc',
          display: 'flex',
          flexDirection: 'column',
          p: theme.spacing(0, 0, 0, 6),
        })}
      >
        {disabledLos.map((lo) => (
          <ListItem
            key={lo.id}
            sx={(theme) => ({
              display: 'list-item',
              p: theme.spacing(0),
              ...theme.typography.body2,
            })}
          >
            {lo.name ?? FALLBACK_LEVEL_NAME}
          </ListItem>
        ))}
      </List>
    </Alert>
  );
}
